/**
 * One form of the checkout, as the prototype's forms step draws it (forms-web.html
 * moment 1 C, after the app's DetailsPage): Back, the form's name and kind, the
 * dots and "N of M forms" when there are two or more, the description, the
 * fields through the app's FormRenderer, an optional row above the button (the
 * registration's consent), and the button. A form that could not be read says
 * so in the app's words with Go Back (FormSheetFailure, row R3).
 */
import type { ReactNode } from 'react';
import { FormRenderer } from '@/components/forms/FormRenderer';
import type { FormStepState } from '@/pages/DetailsPage/FormStepScreen';
import { ctaLabel, formTypeLine } from '@/pages/DetailsPage/formHeader';
import type { FieldError, FormDefinition, FormResponseInput } from '@/types/forms';

import type { PieceContract } from '../contract/define';
import { part, partsOf, pieceRoot } from '../contract/runtime';
import shared from '../styles/shared.module.css';
import { Icon } from './Icon';
import css from './FormSheet.module.css';

export interface FormSheetProps {
  /** The piece drawing the sheet (the forms step or the registration step): its name and parts. */
  readonly piece: Pick<PieceContract, 'name' | 'parts'>;
  readonly form: FormDefinition;
  readonly index: number;
  readonly total: number;
  readonly paymentAhead: boolean;
  readonly submitting: boolean;
  readonly fieldErrors: FieldError[] | undefined;
  readonly backLabel: string;
  readonly onBack: () => void;
  /** True while a step back is under way (row R5). */
  readonly backDisabled?: boolean;
  readonly onSubmit: (responses: FormResponseInput[]) => void;
  readonly onSave: (responses: FormResponseInput[]) => void;
  /** Drawn above the button (the registration's consent row). */
  readonly beforeButton?: ReactNode;
  /** The answers could not be sent (useFormPage's submitError): the line above the button. */
  readonly submitError?: string | null;
}

/** The line above a form's button when its answers could not be sent. */
export function SubmitErrorLine({ text }: { readonly text: string }) {
  return (
    <p className={css.submitError} role="alert" {...part('submitError')}>
      {text}
    </p>
  );
}

export function FormSheet(props: FormSheetProps) {
  const { form, index, total } = props;
  const part = partsOf(props.piece);
  return (
    <div className={css.sheet} {...pieceRoot(props.piece)}>
      <button type="button" className={css.back} onClick={props.onBack} disabled={props.backDisabled} {...part('back', props.backDisabled && 'disabled')}>
        <Icon name="back" small />
        {props.backLabel}
      </button>
      <div className={css.head}>
        <div className={css.headTop}>
          <div>
            <h1 className={css.name} {...part('title')}>
              {form.title}
            </h1>
            <div className={css.kind} {...part('kind')}>
              {formTypeLine(form.formType)}
            </div>
          </div>
          {total >= 2 && (
            <div className={css.count} {...part('count')}>
              <div className={css.dots} aria-hidden="true">
                {Array.from({ length: total }, (_, i) => (
                  <i key={i} className={i === index - 1 ? css.on : undefined} />
                ))}
              </div>
              <div className={css.countLabel}>
                {index} of {total} forms
              </div>
            </div>
          )}
        </div>
        {form.description && (
          <p className={css.desc} {...part('description')}>
            {form.description}
          </p>
        )}
      </div>
      <div className={css.fields} {...part('fields')}>
        <FormRenderer
          form={form}
          onSubmit={props.onSubmit}
          onSave={props.onSave}
          isSubmitting={props.submitting}
          serverErrors={props.fieldErrors}
          disableAutoSave={form.formType === 'REGISTRATION'}
          appearance="clientBooking"
          renderActions={({ formReady, isSubmitting }) => (
            <>
              {props.beforeButton}
              {props.submitError && !isSubmitting && <SubmitErrorLine text={props.submitError} />}
              <div className={css.bar}>
                <button type="submit" className={`${shared.btn} ${shared.btnPrimary} ${css.submit}`} disabled={isSubmitting} {...part('submit', isSubmitting && 'disabled')}>
                  {ctaLabel({ formType: form.formType, isLastForm: index === total, hasPaymentAhead: props.paymentAhead, formReady })}
                  <Icon name="arrow" />
                </button>
              </div>
            </>
          )}
        />
      </div>
    </div>
  );
}

/** A form that could not be read (formFailure, the app's words) and its way back. */
export function FormSheetFailure({ failure, piece }: { failure: Extract<FormStepState, { kind: 'error' }>; piece: Pick<PieceContract, 'name' | 'parts'> }) {
  return (
    <div className={css.sheet} role="alert" {...pieceRoot(piece)} {...partsOf(piece)('failed')}>
      <h1 className={css.name}>{failure.title}</h1>
      <p className={css.desc}>{failure.message}</p>
      {failure.action && (
        <div className={css.bar}>
          <button type="button" className={`${shared.btn} ${shared.btnPrimary} ${css.submit}`} onClick={failure.action.onPress}>
            {failure.action.label}
          </button>
        </div>
      )}
    </div>
  );
}
